import { CloseIcon, SearchIcon, TimeIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Text,
  useOutsideClick,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const RECENT_SEARCHES_KEY = 'recent_searches';
const MAX_RECENT_SEARCHES = 10;

const OmniSearch: React.FC = () => {
  const [search, setSearch] = useState<string>('');
  const [isInputVisible, setIsInputVisible] = useState<boolean>(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [isRecentOpen, setIsRecentOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setRecentSearches(
            parsed.filter(
              (item): item is string => typeof item === 'string'
            )
          );
        }
      }
    } catch (error) {
      console.error(
        'Failed to load recent searches from localStorage',
        error
      );
    }
  }, []);

  useOutsideClick({
    ref: searchContainerRef,
    handler: () => {
      setIsInputVisible(false);
      setIsRecentOpen(false);
    },
  });

  const executeSearch = (rawQuery: string) => {
    const trimmedQuery = rawQuery.trim();

    if (!trimmedQuery) {
      toast({
        title: 'Search Validation',
        description:
          'Please fill in the input field to perform the search.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    navigate(`/search/${encodeURIComponent(trimmedQuery)}`);

    setRecentSearches((prev) => {
      const withoutDuplicate = prev.filter(
        (item) => item.toLowerCase() !== trimmedQuery.toLowerCase()
      );
      const updated = [trimmedQuery, ...withoutDuplicate].slice(
        0,
        MAX_RECENT_SEARCHES
      );
      try {
        localStorage.setItem(
          RECENT_SEARCHES_KEY,
          JSON.stringify(updated)
        );
      } catch (error) {
        console.error(
          'Failed to save recent searches to localStorage',
          error
        );
      }
      return updated;
    });

    setSearch('');
    setIsRecentOpen(false);
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter') {
      executeSearch(search);
    } else if (event.key === 'Escape') {
      setIsRecentOpen(false);
    }
  };

  const clearHistory = () => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (error) {
      console.error(
        'Failed to clear recent searches from localStorage',
        error
      );
    }
    setIsRecentOpen(false);
  };

  const removeRecentItem = (term: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRecentSearches((prev) => {
      const updated = prev.filter((item) => item !== term);
      try {
        if (updated.length === 0) {
          localStorage.removeItem(RECENT_SEARCHES_KEY);
        } else {
          localStorage.setItem(
            RECENT_SEARCHES_KEY,
            JSON.stringify(updated)
          );
        }
      } catch (error) {
        console.error(
          'Failed to update recent searches in localStorage',
          error
        );
      }
      return updated;
    });
  };

  return (
    <Box ref={searchContainerRef} position="relative">
      <Flex gap="1rem" align="center" justify="center">
        {!isInputVisible && (
          <IconButton
            aria-label="Search"
            icon={<SearchIcon />}
            onClick={() => setIsInputVisible(true)}
            variant="ghost"
            color="white"
            _hover={{ bg: 'transparent' }}
          />
        )}

        {isInputVisible && (
          <Box position="relative" w="full" minW="12rem">
            <Input
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDownCapture={handleKeyPress}
              onFocus={() => {
                if (recentSearches.length > 0) {
                  setIsRecentOpen(true);
                }
              }}
              variant="filled"
              borderRadius="0.25rem"
              padding="0rem 0.625rem"
              bg="white"
              color="gray.800"
              _placeholder={{ color: 'gray.500' }}
              border="1px solid"
              focusBorderColor="#3175CE"
              errorBorderColor="red.500"
              _focus={{
                backgroundColor: 'white',
                border: '1px solid #3175CE',
              }}
              autoFocus
            />

            {isRecentOpen && recentSearches.length > 0 && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                mt={1}
                bg="white"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                boxShadow="md"
                zIndex={1000}
                maxH="14rem"
                overflowY="auto"
              >
                <Flex
                  align="center"
                  justify="space-between"
                  px={3}
                  py={2}
                  borderBottomWidth="1px"
                  borderColor="gray.100"
                >
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    color="gray.500"
                  >
                    Recent searches
                  </Text>
                  <Button
                    variant="link"
                    size="xs"
                    color="gray.500"
                    onClick={clearHistory}
                  >
                    Clear history
                  </Button>
                </Flex>

                {recentSearches.map((term) => (
                  <Flex
                    key={term}
                    align="center"
                    justify="space-between"
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'gray.100' }}
                    onClick={() => executeSearch(term)}
                  >
                    <Flex align="center" gap={2}>
                      <TimeIcon boxSize={3} color="gray.400" />
                      <Text
                        fontSize="sm"
                        color="gray.700"
                        noOfLines={1}
                      >
                        {term}
                      </Text>
                    </Flex>
                    <Button
                      variant="ghost"
                      size="xs"
                      aria-label={`Remove ${term} from history`}
                      onClick={(e) => removeRecentItem(term, e)}
                      _hover={{ bg: 'gray.200' }}
                    >
                      <CloseIcon boxSize={2.5} />
                    </Button>
                  </Flex>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default OmniSearch;
